import { faBars, faBorderAll } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { UserfulSearchBar, UserfulTooltip } from "userful-chronos-common-ui/dist";
import { ClickAndDragZone } from "userful-chronos-common-ui/dist/elements";
import EmptyPlaceholder from "../../../common/components/placeholders/EmptyPlaceholder";
import { convertClusterDeviceInfoToTable } from "../../../common/components/UserfulTable/UserfulTableConverter";
import UserfulTable from "../../../common/Table2026/UserfulTable2026";
import { ClusterDeviceInfo, IClusterInfo, IConstellationInfo } from "../../../model/CarbonExternalModels";
import { AppSliceActions } from "../../../store/app/appSlice";
import {
    selectClusterView,
    selectDevicesFilters,
    selectFiltersVisibility,
    selectSearchInput,
    useCarbonDispatch,
    useCarbonSelector,
} from "../../../store/carbonStore";
import { filterDevices } from "../../../store/filters/filtersManager";
import { FiltersActions } from "../../../store/filters/filtersSlice";
import DevicesCard from "../cards/DevicesCard";

interface IProps {
    foundObject: { cluster: IClusterInfo; constellation: IConstellationInfo };
}

export default function DevicesTable(props: IProps) {
    const dispatch = useCarbonDispatch();
    const ZONE_ID = "devices-table";

    const constellation: IConstellationInfo = props.foundObject.constellation;

    const allDeviceData: ClusterDeviceInfo[] = props.foundObject.cluster.clusterDeviceInfo;

    // Visibility
    const filtersVisibility = useCarbonSelector(selectFiltersVisibility);
    const clusterView = useCarbonSelector(selectClusterView);

    // Filtering
    const deviceFilters = useCarbonSelector(selectDevicesFilters);
    const searchInput = useCarbonSelector(selectSearchInput);

    const filteredDevices = filterDevices(deviceFilters, searchInput.devices, allDeviceData);

    // Functions
    const onSearchBarChange = function (input: string) {
        dispatch(FiltersActions.setSearchInput({ ...searchInput, devices: input }));
    };

    const table = convertClusterDeviceInfoToTable(filteredDevices);

    // Renders
    const insertBody = () => {
        if (allDeviceData.length > 0) {
            if (filteredDevices.length > 0) {
                return (
                    <div className="constellationCardsWrapper">
                        {filteredDevices.map((d) => {
                            return <DevicesCard key={d.id.value} device={d} />;
                        })}
                    </div>
                );
            } else {
                return (
                    <EmptyPlaceholder
                        img="./images/notFound.png"
                        title={"No Nodes Found"}
                        description={"Please refine your search/filter criteria and try again."}
                    />
                );
            }
        } else {
            return (
                <EmptyPlaceholder
                    img="./images/notFound.png"
                    title={"No Nodes Assigned"}
                    description={
                        "There are currently no assigned servers in this constellation. To connect a new server, simply click the 'Pairing Code' button. This will generate a unique code that you can use to pair and connect a server to this constellation."
                    }
                />
            );
        }
    };

    return (
        <div className="gcm-contents-wrapper">
            <div className="gcm-contents-header-wrapper">
                <div className="gcm-contents-sub-header-wrapper gcm-contents-sub-header-priority-wrapper">
                    <UserfulSearchBar forceValue={searchInput.devices} onChange={onSearchBarChange} />
                </div>
                <div className="gcm-contents-sub-header-wrapper">
                    <div className="multiStateWrapper">
                        <UserfulTooltip title={"Table View"}>
                            <div
                                className={`mSbutton ${clusterView === "TABLE" ? "active" : ""}`}
                                onClick={(e) => {
                                    dispatch(AppSliceActions.setClusterView("TABLE"));
                                }}
                            >
                                <FontAwesomeIcon icon={faBars} className="mSicon" />
                            </div>
                        </UserfulTooltip>

                        <UserfulTooltip title={"Card View"}>
                            <div
                                className={`mSbutton ${clusterView === "CARDS" ? "active" : ""}`}
                                onClick={(e) => {
                                    dispatch(AppSliceActions.setClusterView("CARDS"));
                                }}
                            >
                                <FontAwesomeIcon icon={faBorderAll} className="mSicon" />
                            </div>
                        </UserfulTooltip>
                    </div>
                </div>
                {filtersVisibility.constellations && <div className="horizontalWrapper"></div>}
            </div>
            <div className="gcm-contents-body-wrapper">
                <div className="constellationCardsWrapper">
                    {clusterView === "TABLE" && allDeviceData.length > 0 && (
                        <ClickAndDragZone id={ZONE_ID}>
                            {filteredDevices.length > 0 ? (
                                <UserfulTable
                                    table={table}
                                    selection={false}
                                    onRowSingleClick={(row, index) => {
                                        const id = (row as any)?.id as StringID;
                                        if (id) {
                                            dispatch(AppSliceActions.setAppView({ type: "DEVICE", id }));
                                        }
                                    }}
                                />
                            ) : (
                                insertBody()
                            )}
                        </ClickAndDragZone>
                    )}
                    {clusterView === "TABLE" && allDeviceData.length === 0 && insertBody()}
                    {clusterView === "CARDS" && insertBody()}
                </div>
            </div>
        </div>
    );
}
